import { ICellRendererParams } from '@amzn/gft-ag-grid-enterprise';
import { useDecklinesContext } from '@context/DecklinesContext';
import { usePageContext } from '@context/PageContext';
import Box from '@mui/material/Box';
import { isEqual } from 'lodash';
import { memo } from 'react';
import { DecklineRowData } from 'types/Page.types';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';

interface SegmentsCellRendererParams {
  valueAccessor?: string;
  hierarchyAccessor?: string;
  inheritedValueClassName?: string;
}

const SegmentsCellRenderer = (params: ICellRendererParams<DecklineRowData> & SegmentsCellRendererParams) => {
  const { inheritedValueClassName, valueAccessor, hierarchyAccessor, data } = params;
  const { subtotalMap } = useDecklinesContext();
  const { modifiedPageProperties } = usePageContext();
  const { page_drivers: currentPageKPIDrivers } = modifiedPageProperties || {};
  const decklineId = data?.rawDecklineData?.deckline_id;

  // Blank lines should not display dimension values
  if (data?.rawDecklineData?.deckline_type === PageSidePanelDropDown.BLANK_LINE) {
    return '';
  }

  // Check if this deckline_id is a key in subtotalMap (meaning it's a subtotal parent)
  if (decklineId && subtotalMap.has(decklineId)) {
    const values = subtotalMap.get(decklineId)!;
    const rowNumbers = values
      .filter((item) => {
        // Check if it's a string AND if the trimmed string has content
        if (typeof item.rowNumber === 'string') {
          return item.rowNumber.trim().length > 0;
        }
        return true;
      })
      .map((item) => Number(item.rowNumber))
      .filter((n) => !Number.isNaN(n)); // Filter out things like "abc" which become NaN
    const start = Math.min(...rowNumbers);
    const end = Math.max(...rowNumbers);

    return (
      <Box className='display-segments' title={`SUM(${start}, ${end})`}>
        SUM({start}:{end})
      </Box>
    );
  }

  if (params.value) {
    // Run tests to determine if `params.value` is a value inherited by Page Driver settings.
    if (
      currentPageKPIDrivers &&
      valueAccessor &&
      hierarchyAccessor &&
      data?.rawDecklineData &&
      inheritedValueClassName
    ) {
      const { deckline_drivers: decklineDrivers } = data.rawDecklineData;

      if (decklineDrivers) {
        // Deckline Driver Data to use for comparison
        const decklineValue = decklineDrivers[valueAccessor as keyof typeof decklineDrivers];
        const decklineHierarchy = decklineDrivers[hierarchyAccessor as keyof typeof decklineDrivers];

        // Null deckline values mean "inherited from page" (cascading)
        const isInherited =
          (decklineValue === null && decklineHierarchy === null) ||
          isEqual(
            { value: decklineValue, hierarchy: decklineHierarchy },
            {
              value: currentPageKPIDrivers[valueAccessor as keyof typeof currentPageKPIDrivers],
              hierarchy: currentPageKPIDrivers[hierarchyAccessor as keyof typeof currentPageKPIDrivers],
            },
          );

        if (isInherited) {
          return (
            <Box className={`display-segments ${inheritedValueClassName}`} title={params?.value}>
              {params?.value}
            </Box>
          );
        }
      }
    }

    // Truncate the displayed text to support better viewability by the User
    // TODO: Can refine if required to use a Tooltip and not just rely on Title attribute
    return (
      <Box className='display-segments' title={params?.value}>
        {params?.value}
      </Box>
    );
  }

  return '';
};

export default memo(SegmentsCellRenderer);
